/**
* NAATI comparison cluster — Japanese / Korean / Vietnamese / Indonesian.
*
* Hand-written per locale (not a machine translation of the Thai or English
* cluster). Facts are aligned with src/data/legal-facts.ts:
* - NAATI certifies translation accuracy; practitioners are listed in the
* NAATI Practitioner Directory (naati.com.au).
* - The Thai Department of Consular Affairs (MFA) legalises the document or
* the official's signature — not translation quality.
* - Thailand acceded to the HCCH Apostille Convention on 30 June 2026; entry
* into force 28 February 2027. Until then the embassy chain applies.
* - Fees and turnaround are approximate ranges; acceptance is always at the
* receiving authority's discretion.
*/
import type { NaatiCompareLocaleCopy, NaatiCompareLocaleItem } from "./compare-locale-types";

const JA_TAIL =
  "2026年8月時点の情報です。要件は変更されることがあるため、提出先機関の最新の案内で必ずご確認ください。受理の可否は提出先機関の裁量です。";
const KO_TAIL =
  "2026년 8월 기준 정보입니다. 요건은 변경될 수 있으므로 제출 기관의 최신 안내를 반드시 확인하시기 바랍니다. 접수 여부는 제출 기관의 재량입니다.";
const VI_TAIL =
  "Thông tin cập nhật đến tháng 8/2026. Yêu cầu có thể thay đổi, vui lòng kiểm tra với cơ quan tiếp nhận trước khi nộp. Việc chấp nhận thuộc thẩm quyền của cơ quan đó.";
const ID_TAIL =
  "Informasi per Agustus 2026. Persyaratan dapat berubah, mohon konfirmasi ke instansi penerima sebelum mengajukan. Penerimaan sepenuhnya kewenangan instansi tersebut.";

const JA_ITEMS: NaatiCompareLocaleItem[] = [
  {
    slug: "naati-vs-consular-legalization",
    title: "NAATI翻訳とタイ外務省（領事局）認証の違い",
    description:
      "NAATI認証が証明するものと、タイ外務省領事局のリーガリゼーションが証明するもの、提出先別の使い分け、所要日数の目安を比較します。",
    h1: "NAATI認証翻訳 vs タイ外務省リーガリゼーション",
    aLabel: "NAATI認証翻訳",
    bLabel: "タイ外務省（領事局）認証",
    answer:
      "証明する対象が異なります。NAATIは「訳文が原文と一致していること」を、NAATI登録翻訳者が署名・押印して証明します。タイ外務省領事局は「タイの文書または担当官の署名が真正であること」を証明し、訳文の品質そのものは対象外です。オーストラリア・ニュージーランドの機関はNAATI訳のみで足りる場合が大半で、それ以外の国やタイ国内で使う場合は領事局＋提出先大使館のルートになります。",
    rows: [
      { aspect: "証明する対象", a: "訳文の正確性", b: "文書・署名の真正性" },
      { aspect: "実施主体", a: "NAATI登録翻訳者（practitioner番号と有効期限）", b: "タイ外務省領事局" },
      { aspect: "主な提出先", a: "豪内務省(DHA)、NZ移民局(INZ)、AHPRA、VETASSESS、豪NZの大学", b: "在タイ各国大使館、タイの官公庁、ハーグ非加盟国向け" },
      { aspect: "所要日数（目安）", a: "営業日で1〜3日", b: "営業日で2〜5日（大使館の待ち時間は別）" },
      { aspect: "確認方法", a: "naati.com.auで翻訳者番号を照会", b: "証明書の登録番号とガルーダ印" },
      { aspect: "原本の要否", a: "高品質なスキャンで足りることが多い", b: "原本または発行機関の認証謄本が必要" },
    ],
    chooseA: [
      "オーストラリアのビザ・永住・市民権申請に提出する",
      "技能査定（VETASSESS・ACS・ANMAC）、AHPRA登録、大学出願に使う",
      "数営業日以内に提出可能なPDFが必要",
    ],
    chooseB: [
      "提出先が豪NZ以外の国である",
      "受理機関が「外務省＋大使館認証」を明記している",
      "タイ国内の法律行為に使用する",
    ],
    faqs: [
      { q: "NAATIと外務省認証の両方が必要ですか。", a: "通常はどちらか一方です。提出先の書面要件に従ってください。同じ書類一式をタイと豪州の双方で使う場合のみ両方が必要になります。" + JA_TAIL },
      { q: "外務省で英訳を認証してもらえば代わりになりますか。", a: "領事局はタイ語⇔英語の翻訳認証を行いますが、豪州の機関が「NAATI」と明記している場合、その代替にはなりません。" },
      { q: "費用はどちらが安いですか。", a: "ページ数と必要な工程数によります。外務省＋大使館ルートは手数料が複数回発生し、期間も長くなる傾向があります。実際の書類一式で見積りをご依頼ください。" },
    ],
  },
  {
    slug: "naati-vs-notary-public",
    title: "NAATI翻訳とタイのノータリー（公証人資格弁護士）の違い",
    description: "訳文の正確性を証明するNAATIと、署名・写しの真正を証明するタイのNotarial Services Attorneyの役割の違いを整理します。",
    h1: "NAATI認証翻訳 vs タイのノータリー",
    aLabel: "NAATI認証翻訳",
    bLabel: "Notarial Services Attorney（タイ）",
    answer:
      "NAATIは訳文の正確性を、タイのノータリー（弁護士会が定める2003年の規則に基づき登録された公証業務弁護士）は署名・写し・宣誓の真正を証明します。したがって「NAATI訳＋ノータリー」は代替関係ではなく補完関係です。タイには英米法系のNotary Publicは存在せず、この登録弁護士が同等の役割を担います。",
    rows: [
      { aspect: "証明する対象", a: "訳文の正確性", b: "署名・写し・宣誓の真正" },
      { aspect: "根拠", a: "NAATI（豪）の資格制度", b: "タイ弁護士会の公証業務規則（2003年）" },
      { aspect: "翻訳の可否", a: "翻訳そのものが業務", b: "翻訳の正確性は証明しない" },
      { aspect: "典型的な用途", a: "豪NZ提出の証明書・成績・戸籍関係", b: "委任状、宣誓供述書、原本証明の写し、パスポート写し" },
    ],
    chooseA: ["豪州・NZの機関にタイ語文書の訳文を提出する"],
    chooseB: ["署名者本人であることの確認や、原本と相違ない写しの証明が必要"],
    faqs: [
      { q: "NAATI訳にノータリーの認証も付けられますか。", a: "可能です。提出先が「訳者の署名の認証」まで求める場合に有効です。" + JA_TAIL },
      { q: "タイのノータリーは訳文の正確性を保証しますか。", a: "いいえ。保証するのは署名や写しの真正であり、訳文の内容ではありません。" },
      { q: "どちらを先に行うべきですか。", a: "一般には翻訳を完成させてから署名認証を行います。順序は提出先の指定に従ってください。" },
    ],
  },
  {
    slug: "naati-vs-apostille",
    title: "NAATI翻訳とアポスティーユの違い（タイは2027年2月28日発効）",
    description: "アポスティーユとNAATI認証は別物です。タイの条約発効日と、それまでの実務上の手順を解説します。",
    h1: "NAATI認証翻訳 vs アポスティーユ",
    aLabel: "NAATI認証翻訳",
    bLabel: "アポスティーユ",
    answer:
      "アポスティーユは公文書の出所（署名・印章）を国際的に証明する制度で、訳文の正確性は対象外です。タイは2026年6月30日にハーグ条約に加入し、2027年2月28日に発効します。発効までは従来どおり外務省領事局＋提出先大使館のリーガリゼーションが必要で、発効後もオーストラリア提出用の訳文にはNAATIが求められる点は変わりません。",
    rows: [
      { aspect: "証明する対象", a: "訳文の正確性", b: "公文書の署名・印章の出所" },
      { aspect: "タイでの利用可否", a: "現在利用可", b: "2027年2月28日発効" },
      { aspect: "対象国", a: "主に豪州・NZ", b: "ハーグ条約加盟国" },
      { aspect: "相互の代替性", a: "アポスティーユの代わりにはならない", b: "NAATI訳の代わりにはならない" },
    ],
    chooseA: ["豪NZの機関に訳文を提出する場合"],
    chooseB: ["ハーグ加盟国の機関が公文書の真正証明を求める場合（2027年2月28日以降）"],
    faqs: [
      { q: "発効前にアポスティーユを取得できますか。", a: "できません。2027年2月28日より前は、外務省領事局と提出先大使館によるリーガリゼーションが標準の手順です。" + JA_TAIL },
      { q: "アポスティーユがあればNAATI訳は不要ですか。", a: "いいえ。証明する対象が異なるため、豪州の機関が求める場合はNAATI訳が別途必要です。" },
      { q: "発効後、手続は簡素になりますか。", a: "加盟国向けは大使館工程が不要になる見込みですが、非加盟国向けは従来どおりです。" },
    ],
  },
  {
    slug: "naati-vs-sworn-translator",
    title: "NAATI認証翻訳と宣誓翻訳者（Sworn Translator）の違い",
    description: "欧州型の宣誓翻訳制度と豪州のNAATI制度の設計思想の違い、提出先による使い分けを比較します。",
    h1: "NAATI認証翻訳 vs 宣誓翻訳者",
    aLabel: "NAATI認証翻訳",
    bLabel: "宣誓翻訳者（欧州型）",
    answer:
      "NAATIは試験に基づく資格認定で、有効期限のある認証番号が付与されます。欧州の宣誓翻訳者は裁判所等に登録し宣誓した翻訳者で、国ごとに制度が異なります。タイには公的な宣誓翻訳者制度がないため、実務上は「翻訳＋外務省認証」または「NAATI訳」のいずれかを提出先の指定に合わせて選びます。",
    rows: [
      { aspect: "資格の根拠", a: "NAATIの認定試験", b: "各国の裁判所・省庁への登録と宣誓" },
      { aspect: "有効期限", a: "あり（更新制）", b: "国により異なる" },
      { aspect: "タイでの制度", a: "タイ在住のNAATI翻訳者に依頼可能", b: "タイには公的制度なし" },
      { aspect: "主な提出先", a: "豪州・NZ", b: "欧州各国、南米の一部" },
    ],
    chooseA: ["提出先が豪州・NZの機関である"],
    chooseB: ["提出先が欧州の宣誓翻訳を明示的に要求している"],
    faqs: [
      { q: "タイで宣誓翻訳者を見つけられますか。", a: "タイに公的な宣誓翻訳制度はありません。多くの場合、外務省領事局の翻訳認証か、当該国大使館の指定翻訳者で代替します。" + JA_TAIL },
      { q: "NAATI訳を欧州で使えますか。", a: "受理される場合もありますが保証はありません。事前に提出先へ書面で確認してください。" },
      { q: "どちらが早いですか。", a: "一般にNAATI訳の方が工程が少なく短期間ですが、提出先の要件が優先します。" },
    ],
  },
  {
    slug: "naati-vs-self-translation",
    title: "自分で翻訳した文書は使えるか（NAATIとの違い）",
    description: "自己翻訳が却下される理由と、機関が独立した資格翻訳者を要求する根拠を説明します。",
    h1: "NAATI認証翻訳 vs 自己翻訳",
    aLabel: "NAATI認証翻訳",
    bLabel: "自己翻訳・知人による翻訳",
    answer:
      "豪州内務省をはじめ多くの機関は、申請者本人や家族による翻訳を認めていません。利害関係のない独立した資格翻訳者であることが要件で、豪州国内で行う翻訳についてはNAATI認証が明示的に求められます。自己翻訳は差戻しの典型的な原因です。",
    rows: [
      { aspect: "独立性", a: "利害関係のない第三者", b: "申請者本人・家族（利害関係あり）" },
      { aspect: "検証可能性", a: "翻訳者番号で照会可能", b: "照会手段なし" },
      { aspect: "差戻しリスク", a: "低い", b: "高い" },
      { aspect: "費用", a: "有料", b: "無料だが再申請費用が発生しうる" },
    ],
    chooseA: ["公的機関・大学・雇用主に提出するすべての場面"],
    chooseB: ["社内参考用など、公式提出を伴わない用途に限る"],
    faqs: [
      { q: "英語が堪能なら自分で翻訳してよいですか。", a: "語学力の問題ではなく独立性の問題です。申請者本人の訳は受理されないのが通常です。" + JA_TAIL },
      { q: "翻訳会社の証明書だけでは足りませんか。", a: "機関がNAATIを明記している場合は不足します。指定文言をご確認ください。" },
      { q: "差戻された場合は作り直せますか。", a: "はい。原本のスキャンをご送付いただければ、NAATI訳を作成し直せます。" },
    ],
  },
  {
    slug: "naati-digital-vs-wet-stamp",
    title: "NAATI電子認証（デジタル）と原本押印の使い分け",
    description: "オンライン提出向けの電子署名付きPDFと、窓口提出向けの実物押印版の違いを比較します。",
    h1: "NAATI電子認証 vs 原本押印",
    aLabel: "電子認証PDF",
    bLabel: "原本押印（ウェットスタンプ）",
    answer:
      "ImmiAccountなどのオンライン提出では、翻訳者のデジタル署名が入ったPDFが標準的に受理されます。窓口提出や一部の大学・登録機関は実物の押印原本を求めることがあります。実務上はPDFを先に発行して締切に間に合わせ、必要に応じて原本を郵送する運用が一般的です。",
    rows: [
      { aspect: "オンライン提出", a: "対応", b: "スキャンして提出" },
      { aspect: "窓口・郵送提出", a: "不可の場合あり", b: "対応" },
      { aspect: "発行の速さ", a: "最短で当日〜翌営業日", b: "配送日数が加算される" },
      { aspect: "改ざん検知", a: "デジタル署名で検証可能", b: "印影と用紙で確認" },
    ],
    chooseA: ["ビザ申請のオンライン提出、締切が近い場合"],
    chooseB: ["原本提出を明記している大学・登録機関"],
    faqs: [
      { q: "原本のスキャンは電子認証と同じですか。", a: "用途は近いものの、電子認証PDFには翻訳者のデジタル署名が含まれ、これを求めるシステムもあります。" + JA_TAIL },
      { q: "両方受け取れますか。", a: "可能です。PDFで締切に対応し、原本は後日郵送する方法が一般的です。" },
      { q: "電子版に有効期限はありますか。", a: "認証自体に期限はありませんが、押印に記載された翻訳者のNAATI資格には有効期限があり、提出日時点で有効であることを求める機関があります。" },
    ],
  },
  {
    slug: "naati-vs-inz-translation",
    title: "豪州DHAとNZ移民局INZの翻訳要件の違い",
    description: "同じ一式の訳文を両国で使えるか、規定の文言の違いから確認します。",
    h1: "DHA（豪州） vs INZ（ニュージーランド）の翻訳要件",
    aLabel: "豪州内務省（DHA）",
    bLabel: "NZ移民局（INZ）",
    answer:
      "いずれも独立した有資格翻訳者による訳文を求めますが、文言が異なります。DHAは豪州国内で行う翻訳についてNAATI認証を要求します。INZは信頼できる独立した翻訳者による訳文と正確性の宣言を求めます。NAATI訳は両方を満たすことが多い一方、逆は必ずしも成立しません。",
    rows: [
      { aspect: "規定の文言", a: "豪州内の翻訳はNAATI認証翻訳者", b: "信頼できる独立した翻訳者＋正確性の宣言" },
      { aspect: "NAATIの受理", a: "可", b: "可" },
      { aspect: "翻訳者の表示", a: "翻訳者番号入りの押印", b: "署名入りの正確性宣言と資格の記載" },
      { aspect: "海外の翻訳者", a: "翻訳者情報の完全な記載が条件", b: "受理される" },
    ],
    chooseA: ["豪州のビザ・市民権・技能査定の書類"],
    chooseB: ["NZの移民申請で正確性宣言が要件となる場合"],
    faqs: [
      { q: "一式で両国に使えますか。", a: "NAATI押印と正確性宣言の双方が備わっていれば通常は可能です。提出前に各機関の文言をご確認ください。" + JA_TAIL },
      { q: "INZはNAATI必須ですか。", a: "INZの案内は信頼できる独立した翻訳者を求めており、NAATIは要件を満たしますが唯一の手段ではありません。" },
      { q: "タイで行った翻訳は受理されますか。", a: "翻訳者の資格と連絡先が記載されていれば通常受理されます。豪州国内で行う翻訳についてはNAATIが明示的に必要です。" },
    ],
  },
  {
    slug: "naati-certified-vs-recognised",
    title: "NAATI Certified と Recognised Practising の違い",
    description: "Certified Translator、Certified Provisional、Recognised Practisingの区分と、提出先が受理する等級を解説します。",
    h1: "NAATI Certified vs Recognised Practising 翻訳者",
    aLabel: "Certified Translator（CT）",
    bLabel: "Recognised Practising Translator（RPT）",
    answer:
      "Certified Translatorは試験に合格して付与される資格で、多くの機関が想定する等級です。Recognised Practisingは、NAATIが試験を実施していない言語について、継続的な実務経験を認める区分です。タイ語⇔英語には認定翻訳者が存在するため、特段の事情がない限りCertified Translatorを選ぶのが安全です。",
    rows: [
      { aspect: "取得方法", a: "認定試験の合格", b: "実務経験に基づく承認（試験なし）" },
      { aspect: "対象言語", a: "試験が実施されている言語（タイ語を含む）", b: "試験が未実施の言語" },
      { aspect: "機関の受理", a: "広く受理される", b: "機関により確認が必要" },
      { aspect: "証明書の表示", a: "翻訳者番号と有効期限", b: "区分名と有効期限" },
    ],
    chooseA: ["タイ語⇔英語の公式提出書類すべて"],
    chooseB: ["認定試験が存在しない言語の場合に限る"],
    faqs: [
      { q: "翻訳者の等級はどこで確認できますか。", a: "naati.com.auの翻訳者ディレクトリで番号を照会すると、区分と有効期限を確認できます。" + JA_TAIL },
      { q: "Provisionalとの違いは何ですか。", a: "Certified Provisionalは経験の浅い段階の区分で、受理しない機関もあります。提出先の指定をご確認ください。" },
      { q: "御社はどちらで対応しますか。", a: "タイ語⇔英語はCertified Translatorが担当し、押印には翻訳者番号と有効期限を記載します。" },
    ],
  },
];

const KO_ITEMS: NaatiCompareLocaleItem[] = [
  {
    slug: "naati-vs-consular-legalization",
    title: "NAATI 번역과 태국 외교부 영사인증의 차이",
    description: "NAATI 인증이 증명하는 것과 태국 외교부 영사국 인증이 증명하는 것, 제출처별 선택 기준과 소요 기간을 비교합니다.",
    h1: "NAATI 인증번역 vs 태국 외교부 영사인증",
    aLabel: "NAATI 인증번역",
    bLabel: "태국 외교부(영사국) 인증",
    answer:
      "증명 대상이 다릅니다. NAATI는 번역문이 원문과 일치함을 NAATI 등록 번역사가 서명·날인해 증명합니다. 태국 외교부 영사국은 태국 문서 또는 담당 공무원 서명이 진정함을 증명하며 번역 품질은 대상이 아닙니다. 호주·뉴질랜드 기관은 대체로 NAATI 번역만으로 충분하고, 그 외 국가나 태국 내 사용은 영사국 인증과 해당국 대사관 인증이 필요합니다.",
    rows: [
      { aspect: "증명 대상", a: "번역의 정확성", b: "문서·서명의 진정성" },
      { aspect: "발급 주체", a: "NAATI 등록 번역사(번호·유효기간)", b: "태국 외교부 영사국" },
      { aspect: "주요 제출처", a: "호주 내무부, 뉴질랜드 이민성, AHPRA, VETASSESS, 대학", b: "주태국 각국 대사관, 태국 관공서" },
      { aspect: "소요 기간(대략)", a: "영업일 1~3일", b: "영업일 2~5일(대사관 대기 별도)" },
      { aspect: "검증 방법", a: "naati.com.au에서 번역사 번호 조회", b: "인증서 등록번호와 가루다 인장" },
      { aspect: "원본 필요 여부", a: "고화질 스캔으로 가능한 경우가 많음", b: "원본 또는 발급기관 인증등본 필요" },
    ],
    chooseA: ["호주 비자·영주권·시민권 신청", "기술심사(VETASSESS·ACS·ANMAC), AHPRA 등록, 대학 지원", "며칠 내 제출 가능한 PDF가 필요한 경우"],
    chooseB: ["제출 국가가 호주·뉴질랜드가 아닌 경우", "접수기관이 외교부+대사관 인증을 명시한 경우", "태국 내 법률행위에 사용하는 경우"],
    faqs: [
      { q: "NAATI와 외교부 인증을 모두 받아야 하나요?", a: "보통은 한 가지만 필요합니다. 제출처의 서면 요건을 따르시고, 동일 서류를 태국과 호주에서 함께 쓸 때만 둘 다 준비합니다." + KO_TAIL },
      { q: "외교부에서 영문 번역을 인증받으면 대체되나요?", a: "영사국도 태-영 번역 인증을 하지만, 호주 기관이 NAATI를 명시했다면 대체되지 않습니다." },
      { q: "어느 쪽이 비용이 적나요?", a: "페이지 수와 필요한 단계에 따라 다릅니다. 외교부+대사관 경로는 수수료가 여러 번 발생하고 기간도 깁니다." },
    ],
  },
  {
    slug: "naati-vs-notary-public",
    title: "NAATI 번역과 태국 공증변호사(Notarial Services Attorney)",
    description: "번역 정확성을 증명하는 NAATI와 서명·사본의 진정을 증명하는 태국 공증변호사의 역할 차이를 정리합니다.",
    h1: "NAATI 인증번역 vs 태국 공증변호사",
    aLabel: "NAATI 인증번역",
    bLabel: "태국 공증변호사",
    answer:
      "NAATI는 번역의 정확성을, 태국 공증변호사(태국 변호사협회 2003년 공증업무 규칙에 따라 등록)는 서명·사본·선서의 진정을 증명합니다. 두 절차는 대체가 아니라 보완 관계입니다. 태국에는 영미식 Notary Public 제도가 없고 등록 변호사가 동등한 역할을 수행합니다.",
    rows: [
      { aspect: "증명 대상", a: "번역의 정확성", b: "서명·사본·선서의 진정" },
      { aspect: "근거", a: "호주 NAATI 자격제도", b: "태국 변호사협회 공증업무 규칙(2003)" },
      { aspect: "번역 수행", a: "번역 자체가 업무", b: "번역 정확성은 증명하지 않음" },
      { aspect: "대표 용도", a: "호주·뉴질랜드 제출용 증명서·성적표", b: "위임장, 진술서, 원본대조필 사본" },
    ],
    chooseA: ["호주·뉴질랜드 기관에 태국 문서 번역본을 제출"],
    chooseB: ["서명자 본인 확인이나 원본대조 사본 증명이 필요"],
    faqs: [
      { q: "NAATI 번역에 공증도 받을 수 있나요?", a: "가능합니다. 제출처가 번역사 서명 인증까지 요구할 때 유용합니다." + KO_TAIL },
      { q: "태국 공증변호사가 번역 정확성을 보장하나요?", a: "아닙니다. 서명과 사본의 진정만 확인합니다." },
      { q: "순서는 어떻게 되나요?", a: "일반적으로 번역 완료 후 서명 인증을 진행하며, 순서는 제출처 지침을 따릅니다." },
    ],
  },
  {
    slug: "naati-vs-apostille",
    title: "NAATI 번역과 아포스티유(태국은 2027년 2월 28일 발효)",
    description: "아포스티유와 NAATI 인증의 차이, 태국의 협약 발효일과 그때까지의 실무 절차를 설명합니다.",
    h1: "NAATI 인증번역 vs 아포스티유",
    aLabel: "NAATI 인증번역",
    bLabel: "아포스티유",
    answer:
      "아포스티유는 공문서의 서명·직인 출처를 국제적으로 증명하는 제도이며 번역 정확성은 대상이 아닙니다. 태국은 2026년 6월 30일 헤이그 협약에 가입했고 2027년 2월 28일 발효합니다. 발효 전에는 기존대로 외교부 영사국과 대사관 인증이 필요하며, 발효 후에도 호주 제출 번역본에는 NAATI가 요구됩니다.",
    rows: [
      { aspect: "증명 대상", a: "번역의 정확성", b: "공문서 서명·직인의 출처" },
      { aspect: "태국 이용 가능", a: "현재 가능", b: "2027년 2월 28일 발효" },
      { aspect: "대상 국가", a: "주로 호주·뉴질랜드", b: "헤이그 협약 가입국" },
      { aspect: "상호 대체", a: "아포스티유를 대체하지 않음", b: "NAATI 번역을 대체하지 않음" },
    ],
    chooseA: ["호주·뉴질랜드 기관에 번역본을 제출"],
    chooseB: ["헤이그 가입국 기관이 공문서 진정 증명을 요구(2027년 2월 28일 이후)"],
    faqs: [
      { q: "발효 전에 아포스티유를 받을 수 있나요?", a: "불가능합니다. 2027년 2월 28일 전에는 외교부와 대사관 인증이 표준 절차입니다." + KO_TAIL },
      { q: "아포스티유가 있으면 NAATI가 불필요한가요?", a: "아닙니다. 증명 대상이 달라 호주 기관이 요구하면 NAATI 번역이 별도로 필요합니다." },
      { q: "발효 후 절차가 간소화되나요?", a: "가입국 대상은 대사관 단계가 줄어들 전망이며, 비가입국은 종전과 같습니다." },
    ],
  },
  {
    slug: "naati-vs-sworn-translator",
    title: "NAATI 인증번역과 선서번역사(Sworn Translator)",
    description: "유럽식 선서번역 제도와 호주 NAATI 제도의 차이, 제출처에 따른 선택 기준을 비교합니다.",
    h1: "NAATI 인증번역 vs 선서번역사",
    aLabel: "NAATI 인증번역",
    bLabel: "선서번역사(유럽식)",
    answer:
      "NAATI는 시험 기반 자격으로 유효기간이 있는 번호가 부여됩니다. 유럽의 선서번역사는 법원 등에 등록·선서한 번역사로 국가별 제도가 다릅니다. 태국에는 공적 선서번역 제도가 없어 실무상 번역+외교부 인증 또는 NAATI 번역 중 제출처 지정에 맞춰 선택합니다.",
    rows: [
      { aspect: "자격 근거", a: "NAATI 인증시험", b: "각국 법원·부처 등록 및 선서" },
      { aspect: "유효기간", a: "있음(갱신제)", b: "국가별 상이" },
      { aspect: "태국 내 제도", a: "태국 거주 NAATI 번역사 의뢰 가능", b: "공적 제도 없음" },
      { aspect: "주요 제출처", a: "호주·뉴질랜드", b: "유럽 각국, 일부 남미" },
    ],
    chooseA: ["제출처가 호주·뉴질랜드 기관"],
    chooseB: ["유럽 기관이 선서번역을 명시적으로 요구"],
    faqs: [
      { q: "태국에서 선서번역사를 구할 수 있나요?", a: "태국에는 공적 선서번역 제도가 없어 외교부 번역인증이나 해당국 대사관 지정 번역사로 대체합니다." + KO_TAIL },
      { q: "NAATI 번역을 유럽에서 쓸 수 있나요?", a: "받아들여지는 경우도 있으나 보장되지 않으므로 사전에 서면 확인을 권합니다." },
      { q: "어느 쪽이 빠른가요?", a: "일반적으로 NAATI 번역이 단계가 적어 빠르지만 제출처 요건이 우선합니다." },
    ],
  },
  {
    slug: "naati-vs-self-translation",
    title: "직접 번역한 문서를 제출할 수 있나요(NAATI와의 차이)",
    description: "자가 번역이 반려되는 이유와 기관이 독립 자격 번역사를 요구하는 근거를 설명합니다.",
    h1: "NAATI 인증번역 vs 자가 번역",
    aLabel: "NAATI 인증번역",
    bLabel: "본인·지인 번역",
    answer:
      "호주 내무부를 비롯한 다수 기관은 신청인 본인이나 가족의 번역을 인정하지 않습니다. 이해관계가 없는 독립 자격 번역사여야 하며, 호주 내에서 수행한 번역은 NAATI 인증이 명시적으로 요구됩니다. 자가 번역은 대표적인 반려 사유입니다.",
    rows: [
      { aspect: "독립성", a: "이해관계 없는 제3자", b: "본인·가족(이해관계 있음)" },
      { aspect: "검증 가능성", a: "번역사 번호로 조회 가능", b: "조회 수단 없음" },
      { aspect: "반려 위험", a: "낮음", b: "높음" },
      { aspect: "비용", a: "유료", b: "무료이나 재신청 비용 발생 가능" },
    ],
    chooseA: ["공공기관·대학·고용주 제출용 모든 문서"],
    chooseB: ["사내 참고용 등 공식 제출이 아닌 용도"],
    faqs: [
      { q: "영어를 잘하면 직접 번역해도 되나요?", a: "어학 능력이 아니라 독립성의 문제로, 본인 번역은 보통 접수되지 않습니다." + KO_TAIL },
      { q: "번역회사 확인서만으로 충분한가요?", a: "기관이 NAATI를 명시했다면 충분하지 않습니다." },
      { q: "반려되면 다시 만들 수 있나요?", a: "가능합니다. 원본 스캔을 보내주시면 NAATI 번역본을 새로 작성합니다." },
    ],
  },
  {
    slug: "naati-digital-vs-wet-stamp",
    title: "NAATI 전자인증과 실물 날인의 차이",
    description: "온라인 제출용 전자서명 PDF와 창구 제출용 실물 날인본의 차이를 비교합니다.",
    h1: "NAATI 전자인증 vs 실물 날인",
    aLabel: "전자인증 PDF",
    bLabel: "실물 날인(웨트 스탬프)",
    answer:
      "ImmiAccount 등 온라인 제출에서는 번역사의 디지털 서명이 포함된 PDF가 일반적으로 접수됩니다. 창구 제출이나 일부 대학·등록기관은 실물 날인본을 요구하기도 합니다. 실무에서는 PDF로 마감을 맞추고 필요 시 원본을 우편 발송합니다.",
    rows: [
      { aspect: "온라인 제출", a: "가능", b: "스캔하여 제출" },
      { aspect: "창구·우편 제출", a: "불가한 경우 있음", b: "가능" },
      { aspect: "발급 속도", a: "당일~익영업일", b: "배송 기간 추가" },
      { aspect: "위변조 확인", a: "디지털 서명으로 검증", b: "인영과 용지로 확인" },
    ],
    chooseA: ["비자 온라인 제출, 마감이 임박한 경우"],
    chooseB: ["원본 제출을 명시한 대학·등록기관"],
    faqs: [
      { q: "원본 스캔이 전자인증과 같나요?", a: "용도는 비슷하지만 전자인증 PDF에는 번역사의 디지털 서명이 포함되어 이를 선호하는 시스템이 있습니다." + KO_TAIL },
      { q: "둘 다 받을 수 있나요?", a: "가능합니다. PDF로 마감에 대응하고 원본은 이후 우편 발송합니다." },
      { q: "전자본에 유효기간이 있나요?", a: "인증 자체는 만료되지 않지만 날인에 표시된 번역사 자격에는 유효기간이 있어 제출일 기준 유효할 것을 요구하는 기관이 있습니다." },
    ],
  },
  {
    slug: "naati-vs-inz-translation",
    title: "호주 DHA와 뉴질랜드 INZ의 번역 요건 차이",
    description: "동일한 번역본을 두 나라에 함께 쓸 수 있는지 규정 문구 차이로 확인합니다.",
    h1: "DHA(호주) vs INZ(뉴질랜드) 번역 요건",
    aLabel: "호주 내무부(DHA)",
    bLabel: "뉴질랜드 이민성(INZ)",
    answer:
      "둘 다 독립적이고 자격 있는 번역사를 요구하지만 문구가 다릅니다. DHA는 호주 내에서 수행한 번역에 NAATI 인증을 요구하고, INZ는 신뢰할 수 있는 독립 번역사와 정확성 진술을 요구합니다. NAATI 번역은 대체로 양쪽을 충족하지만 그 반대는 항상 성립하지 않습니다.",
    rows: [
      { aspect: "규정 문구", a: "호주 내 번역은 NAATI 인증 번역사", b: "신뢰할 수 있는 독립 번역사+정확성 진술" },
      { aspect: "NAATI 수용", a: "가능", b: "가능" },
      { aspect: "번역사 표시", a: "번호가 포함된 날인", b: "서명된 정확성 진술과 자격 기재" },
      { aspect: "해외 번역사", a: "번역사 정보 완전 기재 시 가능", b: "수용" },
    ],
    chooseA: ["호주 비자·시민권·기술심사 서류"],
    chooseB: ["뉴질랜드 이민 신청에서 정확성 진술이 핵심 요건인 경우"],
    faqs: [
      { q: "한 세트로 두 나라에 쓸 수 있나요?", a: "NAATI 날인과 정확성 진술이 모두 있으면 보통 가능하며, 제출 전 각 기관 문구를 확인하세요." + KO_TAIL },
      { q: "INZ에 NAATI가 필수인가요?", a: "INZ 지침은 신뢰할 수 있는 독립 번역사를 요구하며 NAATI는 이를 충족하지만 유일한 방법은 아닙니다." },
      { q: "태국에서 한 번역도 접수되나요?", a: "번역사 자격과 연락처가 기재되어 있으면 일반적으로 접수됩니다." },
    ],
  },
  {
    slug: "naati-certified-vs-recognised",
    title: "NAATI Certified와 Recognised Practising의 차이",
    description: "Certified Translator, Certified Provisional, Recognised Practising 등급과 기관 수용 여부를 설명합니다.",
    h1: "NAATI Certified vs Recognised Practising 번역사",
    aLabel: "Certified Translator(CT)",
    bLabel: "Recognised Practising Translator(RPT)",
    answer:
      "Certified Translator는 시험 합격으로 부여되는 자격으로 대부분의 기관이 기대하는 등급입니다. Recognised Practising은 NAATI가 시험을 시행하지 않는 언어에 대해 최근의 지속적 실무를 인정하는 구분입니다. 태-영은 인증 번역사가 존재하므로 특별한 사정이 없는 한 Certified Translator를 선택하는 것이 안전합니다.",
    rows: [
      { aspect: "취득 방법", a: "인증시험 합격", b: "실무 경력 기반 인정(시험 없음)" },
      { aspect: "대상 언어", a: "시험이 시행되는 언어(태국어 포함)", b: "시험 미시행 언어" },
      { aspect: "기관 수용", a: "폭넓게 수용", b: "기관별 확인 필요" },
      { aspect: "증서 표시", a: "번역사 번호와 유효기간", b: "구분 명칭과 유효기간" },
    ],
    chooseA: ["태-영 공식 제출 서류 전부"],
    chooseB: ["인증시험이 없는 언어에 한정"],
    faqs: [
      { q: "번역사 등급은 어디서 확인하나요?", a: "naati.com.au 번역사 디렉터리에서 번호를 조회하면 구분과 유효기간을 확인할 수 있습니다." + KO_TAIL },
      { q: "Provisional과 무엇이 다른가요?", a: "Certified Provisional은 경력 초기 구분으로 수용하지 않는 기관도 있습니다." },
      { q: "귀사는 어느 등급으로 처리하나요?", a: "태-영은 Certified Translator가 담당하며 날인에 번호와 유효기간을 표시합니다." },
    ],
  },
];

const VI_ITEMS: NaatiCompareLocaleItem[] = [
  {
    slug: "naati-vs-consular-legalization",
    title: "Dịch thuật NAATI và hợp pháp hóa lãnh sự Bộ Ngoại giao Thái Lan",
    description: "NAATI chứng nhận điều gì, hợp pháp hóa lãnh sự chứng nhận điều gì, nơi nào chấp nhận loại nào và thời gian xử lý ước tính.",
    h1: "Bản dịch NAATI vs hợp pháp hóa lãnh sự Thái Lan",
    aLabel: "Bản dịch có chứng nhận NAATI",
    bLabel: "Hợp pháp hóa lãnh sự (Bộ Ngoại giao Thái Lan)",
    answer:
      "Hai thủ tục chứng nhận hai thứ khác nhau. NAATI chứng nhận bản dịch chính xác, do biên dịch viên có trong danh bạ NAATI ký và đóng dấu. Cục Lãnh sự Bộ Ngoại giao Thái Lan chứng nhận tính xác thực của giấy tờ hoặc chữ ký công chức, không đánh giá chất lượng bản dịch. Cơ quan Úc và New Zealand thường chỉ cần bản dịch NAATI; các nước khác hoặc dùng trong Thái Lan thường cần lãnh sự cộng thêm đại sứ quán nước đến.",
    rows: [
      { aspect: "Chứng nhận điều gì", a: "Độ chính xác của bản dịch", b: "Tính xác thực của giấy tờ, chữ ký" },
      { aspect: "Ai chứng nhận", a: "Biên dịch viên NAATI (số hiệu và hạn hiệu lực)", b: "Cục Lãnh sự, Bộ Ngoại giao Thái Lan" },
      { aspect: "Thường được chấp nhận bởi", a: "Bộ Nội vụ Úc, INZ, AHPRA, VETASSESS, các trường đại học", b: "Đại sứ quán tại Thái Lan, cơ quan nhà nước Thái" },
      { aspect: "Thời gian (ước tính)", a: "1–3 ngày làm việc", b: "2–5 ngày làm việc, chưa kể đại sứ quán" },
      { aspect: "Cách kiểm tra", a: "Tra số biên dịch viên trên naati.com.au", b: "Số đăng ký và dấu Garuda trên giấy chứng nhận" },
      { aspect: "Bản gốc", a: "Bản scan chất lượng cao thường đủ", b: "Cần bản gốc hoặc bản sao chứng thực" },
    ],
    chooseA: ["Nộp hồ sơ visa, thường trú, quốc tịch Úc", "Đánh giá tay nghề (VETASSESS, ACS, ANMAC), đăng ký AHPRA, nhập học", "Cần file PDF nộp được trong vài ngày làm việc"],
    chooseB: ["Nước đến không phải Úc hay New Zealand", "Cơ quan tiếp nhận yêu cầu rõ Bộ Ngoại giao và đại sứ quán", "Giấy tờ dùng cho giao dịch pháp lý tại Thái Lan"],
    faqs: [
      { q: "Tôi có cần cả NAATI lẫn hợp pháp hóa lãnh sự không?", a: "Thường chỉ cần một. Hãy theo yêu cầu bằng văn bản của cơ quan tiếp nhận; chỉ cần cả hai khi dùng cùng bộ hồ sơ ở cả Thái Lan và Úc." + VI_TAIL },
      { q: "Bộ Ngoại giao chứng nhận bản dịch tiếng Anh có thay thế được không?", a: "Cục Lãnh sự có chứng nhận bản dịch Thái–Anh, nhưng không thay thế bản dịch NAATI khi cơ quan Úc nêu đích danh NAATI." },
      { q: "Cách nào rẻ hơn?", a: "Tùy số trang và số bước nước đến yêu cầu. Chuỗi Bộ Ngoại giao và đại sứ quán thường phát sinh nhiều khoản phí và mất nhiều thời gian hơn." },
    ],
  },
  {
    slug: "naati-vs-notary-public",
    title: "Bản dịch NAATI và luật sư công chứng Thái Lan",
    description: "Phân biệt vai trò của NAATI (độ chính xác bản dịch) và Notarial Services Attorney Thái Lan (xác thực chữ ký, bản sao).",
    h1: "Bản dịch NAATI vs luật sư công chứng Thái Lan",
    aLabel: "Bản dịch NAATI",
    bLabel: "Notarial Services Attorney (Thái Lan)",
    answer:
      "NAATI chứng nhận bản dịch chính xác; luật sư công chứng Thái Lan (đăng ký theo quy chế dịch vụ công chứng năm 2003 của Liên đoàn Luật sư Thái Lan) xác thực chữ ký, bản sao và lời tuyên thệ. Hai dịch vụ bổ sung cho nhau chứ không thay thế nhau. Thái Lan không có chế định Notary Public kiểu Anh–Mỹ; luật sư đăng ký đảm nhiệm vai trò tương đương.",
    rows: [
      { aspect: "Chứng nhận điều gì", a: "Độ chính xác bản dịch", b: "Chữ ký, bản sao, lời tuyên thệ" },
      { aspect: "Cơ sở pháp lý", a: "Hệ thống chứng nhận NAATI (Úc)", b: "Quy chế dịch vụ công chứng 2003 của Liên đoàn Luật sư Thái" },
      { aspect: "Có dịch không", a: "Chính là công việc dịch", b: "Không chứng nhận nội dung bản dịch" },
      { aspect: "Dùng phổ biến cho", a: "Bằng cấp, học bạ, giấy tờ hộ tịch nộp Úc/NZ", b: "Giấy ủy quyền, bản khai, bản sao y" },
    ],
    chooseA: ["Nộp bản dịch giấy tờ Thái cho cơ quan Úc, New Zealand"],
    chooseB: ["Cần xác nhận người ký hoặc bản sao đúng với bản gốc"],
    faqs: [
      { q: "Có thể công chứng thêm cho bản dịch NAATI không?", a: "Có. Hữu ích khi nơi nhận yêu cầu xác thực chữ ký của biên dịch viên." + VI_TAIL },
      { q: "Luật sư công chứng có bảo đảm bản dịch đúng không?", a: "Không. Họ chỉ xác thực chữ ký và bản sao." },
      { q: "Nên làm bước nào trước?", a: "Thông thường dịch xong rồi mới xác thực chữ ký; thứ tự theo hướng dẫn của nơi nhận." },
    ],
  },
  {
    slug: "naati-vs-apostille",
    title: "Bản dịch NAATI và Apostille (Thái Lan hiệu lực 28/02/2027)",
    description: "Apostille khác NAATI ra sao, mốc hiệu lực của Thái Lan và quy trình áp dụng cho tới thời điểm đó.",
    h1: "Bản dịch NAATI vs Apostille",
    aLabel: "Bản dịch NAATI",
    bLabel: "Apostille",
    answer:
      "Apostille xác nhận nguồn gốc chữ ký và con dấu trên giấy tờ công, không xác nhận độ chính xác bản dịch. Thái Lan gia nhập Công ước Apostille ngày 30/06/2026 và có hiệu lực từ 28/02/2027. Trước mốc đó vẫn áp dụng hợp pháp hóa qua Bộ Ngoại giao và đại sứ quán; sau mốc đó, yêu cầu bản dịch NAATI của cơ quan Úc vẫn không thay đổi.",
    rows: [
      { aspect: "Xác nhận điều gì", a: "Độ chính xác bản dịch", b: "Nguồn gốc chữ ký, con dấu của giấy tờ công" },
      { aspect: "Áp dụng tại Thái Lan", a: "Đang áp dụng", b: "Hiệu lực 28/02/2027" },
      { aspect: "Nước áp dụng", a: "Chủ yếu Úc, New Zealand", b: "Các nước thành viên Công ước" },
      { aspect: "Thay thế lẫn nhau", a: "Không thay Apostille", b: "Không thay bản dịch NAATI" },
    ],
    chooseA: ["Nộp bản dịch cho cơ quan Úc, New Zealand"],
    chooseB: ["Cơ quan nước thành viên yêu cầu xác thực giấy tờ công, từ 28/02/2027"],
    faqs: [
      { q: "Có xin Apostille trước ngày hiệu lực được không?", a: "Không. Trước 28/02/2027, quy trình chuẩn vẫn là Bộ Ngoại giao rồi đại sứ quán." + VI_TAIL },
      { q: "Có Apostille rồi thì khỏi cần NAATI?", a: "Không. Hai thứ xác nhận khác nhau; cơ quan Úc yêu cầu thì vẫn cần bản dịch NAATI." },
      { q: "Sau khi hiệu lực thủ tục có gọn hơn?", a: "Với nước thành viên sẽ bỏ bước đại sứ quán; với nước ngoài Công ước vẫn như cũ." },
    ],
  },
  {
    slug: "naati-vs-sworn-translator",
    title: "NAATI và biên dịch viên tuyên thệ (sworn translator)",
    description: "So sánh mô hình tuyên thệ kiểu châu Âu với hệ thống chứng nhận NAATI của Úc và cách chọn theo nơi nộp.",
    h1: "Bản dịch NAATI vs biên dịch viên tuyên thệ",
    aLabel: "Bản dịch NAATI",
    bLabel: "Biên dịch viên tuyên thệ (châu Âu)",
    answer:
      "NAATI cấp chứng nhận dựa trên kỳ thi, kèm số hiệu có hạn hiệu lực. Biên dịch viên tuyên thệ ở châu Âu đăng ký và tuyên thệ trước tòa án hoặc bộ chủ quản, quy định khác nhau theo từng nước. Thái Lan không có chế định tuyên thệ chính thức, nên thực tế chọn giữa bản dịch kèm chứng nhận Bộ Ngoại giao hoặc bản dịch NAATI tùy yêu cầu nơi nhận.",
    rows: [
      { aspect: "Cơ sở tư cách", a: "Kỳ thi chứng nhận NAATI", b: "Đăng ký và tuyên thệ tại tòa án, bộ" },
      { aspect: "Hạn hiệu lực", a: "Có, phải gia hạn", b: "Tùy quốc gia" },
      { aspect: "Tại Thái Lan", a: "Có biên dịch viên NAATI nhận việc", b: "Không có chế định chính thức" },
      { aspect: "Nơi nộp phổ biến", a: "Úc, New Zealand", b: "Các nước châu Âu, một số nước Nam Mỹ" },
    ],
    chooseA: ["Nơi nhận là cơ quan Úc hoặc New Zealand"],
    chooseB: ["Cơ quan châu Âu yêu cầu rõ bản dịch tuyên thệ"],
    faqs: [
      { q: "Ở Thái Lan tìm biên dịch viên tuyên thệ được không?", a: "Thái Lan không có chế định này; thường thay bằng chứng nhận bản dịch của Bộ Ngoại giao hoặc biên dịch viên do đại sứ quán chỉ định." + VI_TAIL },
      { q: "Bản dịch NAATI dùng ở châu Âu được không?", a: "Có nơi chấp nhận nhưng không bảo đảm; nên hỏi bằng văn bản trước." },
      { q: "Cách nào nhanh hơn?", a: "Thường bản dịch NAATI ít bước hơn nên nhanh hơn, nhưng yêu cầu của nơi nhận là quyết định." },
    ],
  },
  {
    slug: "naati-vs-self-translation",
    title: "Tự dịch giấy tờ có được chấp nhận không",
    description: "Vì sao bản tự dịch bị từ chối và vì sao cơ quan yêu cầu biên dịch viên độc lập có chứng nhận.",
    h1: "Bản dịch NAATI vs tự dịch",
    aLabel: "Bản dịch NAATI",
    bLabel: "Tự dịch hoặc nhờ người quen",
    answer:
      "Bộ Nội vụ Úc và nhiều cơ quan khác không chấp nhận bản dịch do đương đơn hoặc người thân thực hiện. Yêu cầu là biên dịch viên độc lập, không có lợi ích liên quan; với bản dịch thực hiện tại Úc thì phải có chứng nhận NAATI. Tự dịch là một trong những lý do bị trả hồ sơ phổ biến nhất.",
    rows: [
      { aspect: "Tính độc lập", a: "Bên thứ ba không liên quan lợi ích", b: "Đương đơn, người thân" },
      { aspect: "Khả năng kiểm chứng", a: "Tra được số biên dịch viên", b: "Không tra được" },
      { aspect: "Rủi ro bị trả hồ sơ", a: "Thấp", b: "Cao" },
      { aspect: "Chi phí", a: "Có phí dịch vụ", b: "Miễn phí nhưng có thể phát sinh phí nộp lại" },
    ],
    chooseA: ["Mọi hồ sơ nộp cơ quan nhà nước, trường học, nhà tuyển dụng"],
    chooseB: ["Chỉ dùng tham khảo nội bộ, không nộp chính thức"],
    faqs: [
      { q: "Tôi giỏi tiếng Anh thì tự dịch được không?", a: "Vấn đề không nằm ở trình độ mà ở tính độc lập; bản dịch của chính đương đơn thường không được nhận." + VI_TAIL },
      { q: "Giấy xác nhận của công ty dịch thuật có đủ không?", a: "Không đủ nếu cơ quan nêu rõ NAATI." },
      { q: "Bị trả hồ sơ thì làm lại được không?", a: "Được. Gửi bản scan gốc, chúng tôi làm lại bản dịch NAATI." },
    ],
  },
  {
    slug: "naati-digital-vs-wet-stamp",
    title: "Chứng nhận NAATI bản điện tử và bản đóng dấu mực",
    description: "Khi nào nộp PDF có chữ ký số, khi nào cần bản đóng dấu mực thật.",
    h1: "NAATI bản điện tử vs bản đóng dấu mực",
    aLabel: "PDF chứng nhận điện tử",
    bLabel: "Bản đóng dấu mực",
    answer:
      "Hồ sơ nộp trực tuyến như ImmiAccount thường chấp nhận PDF có chữ ký số của biên dịch viên. Nộp trực tiếp tại quầy hoặc một số trường, hội đồng nghề nghiệp có thể yêu cầu bản đóng dấu mực. Thực tế nên lấy PDF trước cho kịp hạn, rồi gửi bản giấy qua bưu điện nếu cần.",
    rows: [
      { aspect: "Nộp trực tuyến", a: "Được", b: "Phải scan lại" },
      { aspect: "Nộp trực tiếp, bưu điện", a: "Có nơi không nhận", b: "Được" },
      { aspect: "Tốc độ", a: "Trong ngày đến ngày làm việc kế tiếp", b: "Cộng thêm thời gian chuyển phát" },
      { aspect: "Chống giả mạo", a: "Kiểm tra bằng chữ ký số", b: "Nhận biết qua dấu và giấy" },
    ],
    chooseA: ["Nộp visa trực tuyến, sắp đến hạn"],
    chooseB: ["Trường hoặc hội đồng yêu cầu bản giấy gốc"],
    faqs: [
      { q: "Scan bản giấy có bằng bản điện tử không?", a: "Công dụng gần giống nhau, nhưng PDF chứng nhận điện tử có chữ ký số của biên dịch viên và một số hệ thống ưu tiên định dạng này." + VI_TAIL },
      { q: "Có thể lấy cả hai không?", a: "Có. Dùng PDF cho kịp hạn và nhận bản giấy sau." },
      { q: "Bản điện tử có hết hạn không?", a: "Chứng nhận không hết hạn, nhưng chứng chỉ NAATI của biên dịch viên ghi trên dấu có hạn hiệu lực và một số nơi yêu cầu còn hiệu lực vào ngày nộp." },
    ],
  },
  {
    slug: "naati-vs-inz-translation",
    title: "Khác biệt yêu cầu dịch thuật giữa DHA (Úc) và INZ (New Zealand)",
    description: "Một bộ bản dịch có dùng được cho cả hai nước không, xét theo câu chữ trong quy định.",
    h1: "DHA (Úc) vs INZ (New Zealand): yêu cầu bản dịch",
    aLabel: "Bộ Nội vụ Úc (DHA)",
    bLabel: "Cơ quan Di trú New Zealand (INZ)",
    answer:
      "Cả hai đều yêu cầu biên dịch viên độc lập, có năng lực, nhưng câu chữ khác nhau. DHA yêu cầu bản dịch thực hiện tại Úc phải do biên dịch viên có chứng nhận NAATI. INZ chấp nhận biên dịch viên độc lập, uy tín kèm tuyên bố về độ chính xác. Bản dịch NAATI thường đáp ứng cả hai, chiều ngược lại không luôn đúng.",
    rows: [
      { aspect: "Câu chữ quy định", a: "Dịch tại Úc phải là biên dịch viên NAATI", b: "Biên dịch viên độc lập, uy tín kèm tuyên bố" },
      { aspect: "Chấp nhận NAATI", a: "Có", b: "Có" },
      { aspect: "Thông tin biên dịch viên", a: "Dấu kèm số hiệu", b: "Tuyên bố có chữ ký, nêu trình độ" },
      { aspect: "Biên dịch viên ở nước ngoài", a: "Được nếu ghi đủ thông tin", b: "Được chấp nhận" },
    ],
    chooseA: ["Hồ sơ visa, quốc tịch, đánh giá tay nghề Úc"],
    chooseB: ["Hồ sơ di trú New Zealand cần tuyên bố về độ chính xác"],
    faqs: [
      { q: "Một bộ dùng cho cả hai nước được không?", a: "Thường được nếu có cả dấu NAATI và tuyên bố độ chính xác; hãy đối chiếu câu chữ của từng cơ quan trước khi nộp." + VI_TAIL },
      { q: "INZ có bắt buộc NAATI không?", a: "Hướng dẫn của INZ yêu cầu biên dịch viên độc lập, uy tín; NAATI đáp ứng nhưng không phải lựa chọn duy nhất." },
      { q: "Bản dịch làm tại Thái Lan có được nhận?", a: "Thường được nếu ghi rõ trình độ và thông tin liên hệ của biên dịch viên." },
    ],
  },
  {
    slug: "naati-certified-vs-recognised",
    title: "Khác biệt giữa NAATI Certified và Recognised Practising",
    description: "Phân biệt Certified Translator, Certified Provisional và Recognised Practising, và mức nào được cơ quan chấp nhận.",
    h1: "NAATI Certified vs Recognised Practising",
    aLabel: "Certified Translator (CT)",
    bLabel: "Recognised Practising Translator (RPT)",
    answer:
      "Certified Translator là tư cách đạt được qua kỳ thi và là mức mà hầu hết cơ quan mong đợi. Recognised Practising áp dụng cho các ngôn ngữ NAATI chưa tổ chức thi, ghi nhận kinh nghiệm hành nghề gần đây chứ không phải kết quả thi. Cặp Thái–Anh có biên dịch viên đạt chứng nhận nên hãy chọn Certified Translator trừ khi có lý do đặc biệt.",
    rows: [
      { aspect: "Cách đạt được", a: "Thi đỗ kỳ thi chứng nhận", b: "Công nhận theo kinh nghiệm, không thi" },
      { aspect: "Ngôn ngữ áp dụng", a: "Ngôn ngữ có kỳ thi, gồm tiếng Thái", b: "Ngôn ngữ chưa có kỳ thi" },
      { aspect: "Mức độ chấp nhận", a: "Rộng rãi", b: "Cần hỏi trước từng cơ quan" },
      { aspect: "Ghi trên chứng nhận", a: "Số hiệu và hạn hiệu lực", b: "Tên phân loại và hạn hiệu lực" },
    ],
    chooseA: ["Mọi hồ sơ chính thức cặp ngôn ngữ Thái–Anh"],
    chooseB: ["Chỉ khi ngôn ngữ đó chưa có kỳ thi chứng nhận"],
    faqs: [
      { q: "Kiểm tra cấp bậc biên dịch viên ở đâu?", a: "Tra số hiệu trong danh bạ biên dịch viên trên naati.com.au để xem phân loại và hạn hiệu lực." + VI_TAIL },
      { q: "Certified Provisional khác gì?", a: "Đây là mức dành cho người mới hành nghề; một số cơ quan không chấp nhận." },
      { q: "Bên bạn dùng cấp nào?", a: "Cặp Thái–Anh do Certified Translator thực hiện, dấu ghi số hiệu và hạn hiệu lực." },
    ],
  },
];

const ID_ITEMS: NaatiCompareLocaleItem[] = [
  {
    slug: "naati-vs-consular-legalization",
    title: "Terjemahan NAATI vs legalisasi konsuler Kemlu Thailand",
    description: "Apa yang disahkan NAATI, apa yang disahkan Departemen Urusan Konsuler Thailand, instansi mana menerima yang mana, dan perkiraan waktu proses.",
    h1: "Terjemahan bersertifikat NAATI vs legalisasi Kemlu Thailand",
    aLabel: "Terjemahan bersertifikat NAATI",
    bLabel: "Legalisasi konsuler Kemlu Thailand",
    answer:
      "Keduanya mengesahkan hal berbeda. NAATI mengesahkan bahwa terjemahan akurat, ditandatangani penerjemah yang terdaftar di direktori NAATI. Departemen Urusan Konsuler Kemlu Thailand mengesahkan keaslian dokumen atau tanda tangan pejabat, bukan mutu terjemahan. Instansi Australia dan Selandia Baru umumnya cukup dengan terjemahan NAATI; tujuan lain atau penggunaan di Thailand biasanya memerlukan jalur konsuler ditambah kedutaan negara tujuan.",
    rows: [
      { aspect: "Yang disahkan", a: "Keakuratan terjemahan", b: "Keaslian dokumen atau tanda tangan pejabat" },
      { aspect: "Pihak pengesah", a: "Penerjemah bersertifikat NAATI (nomor dan masa berlaku)", b: "Departemen Urusan Konsuler, Kemlu Thailand" },
      { aspect: "Umumnya diterima oleh", a: "DHA Australia, INZ, AHPRA, VETASSESS, universitas", b: "Kedutaan asing di Thailand, instansi pemerintah Thailand" },
      { aspect: "Waktu (perkiraan)", a: "1–3 hari kerja", b: "2–5 hari kerja, di luar antrean kedutaan" },
      { aspect: "Cara verifikasi", a: "Cek nomor penerjemah di naati.com.au", b: "Nomor registrasi dan stempel Garuda" },
      { aspect: "Dokumen asli", a: "Pindaian berkualitas biasanya cukup", b: "Perlu asli atau salinan resmi dari instansi penerbit" },
    ],
    chooseA: ["Pengajuan visa, PR, atau kewarganegaraan Australia", "Penilaian keterampilan (VETASSESS, ACS, ANMAC), registrasi AHPRA, pendaftaran kuliah", "Butuh PDF siap unggah dalam beberapa hari kerja"],
    chooseB: ["Negara tujuan bukan Australia atau Selandia Baru", "Instansi penerima menyebut Kemlu plus kedutaan", "Dokumen dipakai untuk perbuatan hukum di Thailand"],
    faqs: [
      { q: "Apakah perlu NAATI dan legalisasi sekaligus?", a: "Umumnya cukup salah satu. Ikuti persyaratan tertulis instansi penerima; keduanya hanya perlu bila berkas yang sama dipakai di Thailand dan Australia." + ID_TAIL },
      { q: "Bisakah Kemlu mengesahkan terjemahan Inggris sebagai gantinya?", a: "Departemen Urusan Konsuler memang mengesahkan terjemahan Thai–Inggris, tetapi itu tidak menggantikan terjemahan NAATI bila instansi Australia menyebut NAATI secara khusus." },
      { q: "Mana yang lebih murah?", a: "Tergantung jumlah halaman dan langkah yang diminta negara tujuan. Jalur Kemlu plus kedutaan biasanya melibatkan beberapa biaya terpisah dan waktu lebih lama." },
    ],
  },
  {
    slug: "naati-vs-notary-public",
    title: "Terjemahan NAATI vs advokat notarial Thailand",
    description: "Beda peran NAATI yang mengesahkan terjemahan dengan Notarial Services Attorney yang mengesahkan tanda tangan dan salinan.",
    h1: "Terjemahan NAATI vs Notarial Services Attorney",
    aLabel: "Terjemahan NAATI",
    bLabel: "Notarial Services Attorney (Thailand)",
    answer:
      "NAATI mengesahkan keakuratan terjemahan; advokat notarial Thailand, yang terdaftar berdasarkan peraturan layanan notarial Lawyers Council tahun 2003, mengesahkan tanda tangan, salinan, dan sumpah. Keduanya saling melengkapi, bukan saling menggantikan. Thailand tidak memiliki lembaga Notary Public ala common law; advokat terdaftarlah yang menjalankan peran setara.",
    rows: [
      { aspect: "Yang disahkan", a: "Keakuratan terjemahan", b: "Tanda tangan, salinan, sumpah" },
      { aspect: "Dasar hukum", a: "Sistem sertifikasi NAATI (Australia)", b: "Peraturan layanan notarial Lawyers Council 2003" },
      { aspect: "Melakukan penerjemahan", a: "Ya, itu pekerjaannya", b: "Tidak menilai isi terjemahan" },
      { aspect: "Contoh pemakaian", a: "Ijazah, transkrip, dokumen sipil untuk Australia/NZ", b: "Surat kuasa, afidavit, salinan sesuai asli" },
    ],
    chooseA: ["Mengirim terjemahan dokumen Thailand ke instansi Australia atau Selandia Baru"],
    chooseB: ["Perlu pengesahan identitas penanda tangan atau salinan sesuai asli"],
    faqs: [
      { q: "Bisakah terjemahan NAATI dinotariskan juga?", a: "Bisa, berguna bila penerima meminta pengesahan tanda tangan penerjemah." + ID_TAIL },
      { q: "Apakah advokat notarial menjamin terjemahan benar?", a: "Tidak. Yang dijamin hanya keaslian tanda tangan dan salinan." },
      { q: "Mana dulu yang dikerjakan?", a: "Umumnya terjemahan diselesaikan lebih dulu, baru pengesahan tanda tangan, kecuali penerima menentukan urutan lain." },
    ],
  },
  {
    slug: "naati-vs-apostille",
    title: "Terjemahan NAATI vs Apostille (Thailand berlaku 28 Februari 2027)",
    description: "Perbedaan apostille dan sertifikasi NAATI, tanggal berlakunya untuk Thailand, dan prosedur sampai saat itu.",
    h1: "Terjemahan NAATI vs Apostille",
    aLabel: "Terjemahan NAATI",
    bLabel: "Apostille",
    answer:
      "Apostille mengesahkan asal tanda tangan dan cap pada dokumen publik, bukan keakuratan terjemahan. Thailand mengaksesi Konvensi Apostille pada 30 Juni 2026 dan berlaku 28 Februari 2027. Sebelum tanggal itu, jalur Kemlu ditambah kedutaan tetap berlaku; setelahnya, permintaan terjemahan NAATI dari instansi Australia tetap tidak berubah.",
    rows: [
      { aspect: "Yang disahkan", a: "Keakuratan terjemahan", b: "Asal tanda tangan dan cap dokumen publik" },
      { aspect: "Tersedia di Thailand", a: "Sudah tersedia", b: "Berlaku 28 Februari 2027" },
      { aspect: "Negara terkait", a: "Terutama Australia dan Selandia Baru", b: "Negara anggota Konvensi" },
      { aspect: "Saling menggantikan", a: "Tidak menggantikan apostille", b: "Tidak menggantikan terjemahan NAATI" },
    ],
    chooseA: ["Menyerahkan terjemahan ke instansi Australia atau Selandia Baru"],
    chooseB: ["Instansi negara anggota meminta pengesahan dokumen publik, mulai 28 Februari 2027"],
    faqs: [
      { q: "Bisakah mengurus apostille sebelum tanggal berlaku?", a: "Tidak. Sebelum 28 Februari 2027 prosedur bakunya adalah Kemlu lalu kedutaan negara tujuan." + ID_TAIL },
      { q: "Kalau sudah apostille, masih perlu NAATI?", a: "Masih, karena keduanya mengesahkan hal berbeda." },
      { q: "Apakah prosesnya jadi lebih singkat setelah berlaku?", a: "Untuk negara anggota, langkah kedutaan diperkirakan tidak lagi diperlukan; untuk non-anggota tetap seperti sekarang." },
    ],
  },
  {
    slug: "naati-vs-sworn-translator",
    title: "NAATI vs penerjemah tersumpah",
    description: "Membandingkan sistem penerjemah tersumpah ala Eropa dengan sertifikasi NAATI Australia serta cara memilihnya.",
    h1: "Terjemahan NAATI vs penerjemah tersumpah",
    aLabel: "Terjemahan NAATI",
    bLabel: "Penerjemah tersumpah (model Eropa)",
    answer:
      "NAATI adalah sertifikasi berbasis ujian dengan nomor penerjemah bermasa berlaku. Penerjemah tersumpah di Eropa terdaftar dan disumpah di pengadilan atau kementerian, dengan aturan berbeda tiap negara. Thailand tidak memiliki sistem penerjemah tersumpah resmi, sehingga praktiknya memilih antara terjemahan plus pengesahan Kemlu atau terjemahan NAATI sesuai permintaan penerima.",
    rows: [
      { aspect: "Dasar kualifikasi", a: "Ujian sertifikasi NAATI", b: "Pendaftaran dan sumpah di pengadilan atau kementerian" },
      { aspect: "Masa berlaku", a: "Ada, perlu perpanjangan", b: "Berbeda tiap negara" },
      { aspect: "Di Thailand", a: "Ada penerjemah NAATI yang bisa dipakai", b: "Tidak ada sistem resmi" },
      { aspect: "Tujuan umum", a: "Australia dan Selandia Baru", b: "Negara Eropa dan sebagian Amerika Latin" },
    ],
    chooseA: ["Penerima adalah instansi Australia atau Selandia Baru"],
    chooseB: ["Instansi Eropa secara tegas meminta terjemahan tersumpah"],
    faqs: [
      { q: "Adakah penerjemah tersumpah di Thailand?", a: "Tidak ada sistem resmi; biasanya diganti pengesahan terjemahan oleh Kemlu atau penerjemah yang ditunjuk kedutaan." + ID_TAIL },
      { q: "Bisakah terjemahan NAATI dipakai di Eropa?", a: "Kadang diterima, tetapi tidak dijamin; mintalah konfirmasi tertulis lebih dulu." },
      { q: "Mana yang lebih cepat?", a: "Biasanya NAATI karena langkahnya lebih sedikit, namun persyaratan penerima yang menentukan." },
    ],
  },
  {
    slug: "naati-vs-self-translation",
    title: "Bolehkah menerjemahkan dokumen sendiri",
    description: "Alasan terjemahan sendiri ditolak dan mengapa instansi menuntut penerjemah independen bersertifikat.",
    h1: "Terjemahan NAATI vs terjemahan sendiri",
    aLabel: "Terjemahan NAATI",
    bLabel: "Terjemahan sendiri atau oleh kerabat",
    answer:
      "DHA Australia dan banyak instansi lain tidak menerima terjemahan yang dibuat pemohon atau keluarganya. Syaratnya penerjemah independen tanpa benturan kepentingan, dan untuk terjemahan yang dikerjakan di Australia disyaratkan sertifikasi NAATI. Terjemahan sendiri adalah penyebab penolakan yang paling sering ditemui.",
    rows: [
      { aspect: "Independensi", a: "Pihak ketiga tanpa kepentingan", b: "Pemohon atau keluarga" },
      { aspect: "Dapat diverifikasi", a: "Nomor penerjemah bisa dicek", b: "Tidak ada sarana pengecekan" },
      { aspect: "Risiko ditolak", a: "Rendah", b: "Tinggi" },
      { aspect: "Biaya", a: "Berbayar", b: "Gratis, tetapi berpotensi biaya pengajuan ulang" },
    ],
    chooseA: ["Semua berkas untuk instansi pemerintah, kampus, atau pemberi kerja"],
    chooseB: ["Hanya untuk keperluan internal, bukan pengajuan resmi"],
    faqs: [
      { q: "Kalau bahasa Inggris saya bagus, boleh menerjemahkan sendiri?", a: "Persoalannya bukan kemampuan bahasa melainkan independensi; terjemahan pemohon biasanya tidak diterima." + ID_TAIL },
      { q: "Apakah surat keterangan biro penerjemah cukup?", a: "Tidak cukup bila instansi menyebut NAATI secara khusus." },
      { q: "Kalau ditolak, bisa dibuat ulang?", a: "Bisa. Kirimkan pindaian dokumen asli dan kami buatkan terjemahan NAATI." },
    ],
  },
  {
    slug: "naati-digital-vs-wet-stamp",
    title: "Sertifikasi NAATI digital vs stempel basah",
    description: "Kapan PDF bertanda tangan digital cukup dan kapan diperlukan lembar bercap basah.",
    h1: "Sertifikasi NAATI digital vs stempel basah",
    aLabel: "PDF sertifikasi digital",
    bLabel: "Stempel basah",
    answer:
      "Pengajuan daring seperti ImmiAccount umumnya menerima PDF dengan tanda tangan digital penerjemah. Penyerahan langsung di loket atau sebagian universitas dan badan registrasi bisa meminta lembar bercap basah. Praktik yang lazim: ambil PDF dulu untuk mengejar tenggat, lalu kirim lembar fisik bila diperlukan.",
    rows: [
      { aspect: "Pengajuan daring", a: "Didukung", b: "Perlu dipindai dulu" },
      { aspect: "Penyerahan langsung", a: "Kadang tidak diterima", b: "Didukung" },
      { aspect: "Kecepatan", a: "Hari yang sama sampai hari kerja berikutnya", b: "Ditambah waktu pengiriman" },
      { aspect: "Deteksi pemalsuan", a: "Diverifikasi lewat tanda tangan digital", b: "Dikenali dari cap dan kertas" },
    ],
    chooseA: ["Pengajuan visa daring dan tenggat yang dekat"],
    chooseB: ["Universitas atau badan yang mensyaratkan lembar asli"],
    faqs: [
      { q: "Apakah pindaian lembar basah sama dengan sertifikasi digital?", a: "Fungsinya mirip, tetapi PDF sertifikasi digital memuat tanda tangan digital penerjemah dan sebagian sistem lebih menyukainya." + ID_TAIL },
      { q: "Bisakah minta keduanya?", a: "Bisa. Gunakan PDF untuk tenggat, lembar fisik menyusul." },
      { q: "Apakah versi digital kedaluwarsa?", a: "Sertifikasinya tidak kedaluwarsa, tetapi sertifikat NAATI penerjemah pada cap punya masa berlaku dan sebagian instansi meminta masih berlaku pada tanggal pengajuan." },
    ],
  },
  {
    slug: "naati-vs-inz-translation",
    title: "Beda syarat terjemahan DHA Australia dan INZ Selandia Baru",
    description: "Bisakah satu set terjemahan dipakai untuk dua negara, ditinjau dari rumusan aturannya.",
    h1: "DHA (Australia) vs INZ (Selandia Baru)",
    aLabel: "Department of Home Affairs (DHA)",
    bLabel: "Immigration New Zealand (INZ)",
    answer:
      "Keduanya menuntut penerjemah independen dan kompeten, tetapi rumusannya berbeda. DHA mensyaratkan terjemahan yang dikerjakan di Australia dibuat penerjemah bersertifikat NAATI. INZ menerima penerjemah independen yang bereputasi disertai pernyataan keakuratan. Terjemahan NAATI biasanya memenuhi keduanya, sedangkan sebaliknya belum tentu.",
    rows: [
      { aspect: "Rumusan aturan", a: "Terjemahan di Australia harus penerjemah NAATI", b: "Penerjemah independen bereputasi plus pernyataan" },
      { aspect: "Menerima NAATI", a: "Ya", b: "Ya" },
      { aspect: "Keterangan penerjemah", a: "Cap dengan nomor penerjemah", b: "Pernyataan keakuratan bertanda tangan dan kualifikasi" },
      { aspect: "Penerjemah di luar negeri", a: "Boleh bila datanya lengkap", b: "Diterima" },
    ],
    chooseA: ["Berkas visa, kewarganegaraan, atau penilaian keterampilan Australia"],
    chooseB: ["Pengajuan imigrasi Selandia Baru yang menekankan pernyataan keakuratan"],
    faqs: [
      { q: "Satu set untuk dua negara bisa?", a: "Umumnya bisa bila memuat cap NAATI sekaligus pernyataan keakuratan; periksa rumusan masing-masing instansi sebelum mengirim." + ID_TAIL },
      { q: "Apakah INZ mewajibkan NAATI?", a: "Panduan INZ meminta penerjemah independen yang bereputasi; NAATI memenuhinya tetapi bukan satu-satunya jalur." },
      { q: "Apakah terjemahan dari Thailand diterima?", a: "Umumnya diterima bila kualifikasi dan kontak penerjemah tercantum." },
    ],
  },
  {
    slug: "naati-certified-vs-recognised",
    title: "Beda NAATI Certified dan Recognised Practising",
    description: "Penjelasan tingkat Certified Translator, Certified Provisional, dan Recognised Practising serta penerimaannya.",
    h1: "NAATI Certified vs Recognised Practising",
    aLabel: "Certified Translator (CT)",
    bLabel: "Recognised Practising Translator (RPT)",
    answer:
      "Certified Translator diperoleh melalui ujian dan merupakan tingkat yang diharapkan sebagian besar instansi. Recognised Practising berlaku untuk bahasa yang belum diujikan NAATI, membuktikan praktik berkelanjutan, bukan kelulusan ujian. Pasangan Thai–Inggris memiliki penerjemah bersertifikat, jadi pilihlah Certified Translator kecuali ada alasan khusus.",
    rows: [
      { aspect: "Cara memperoleh", a: "Lulus ujian sertifikasi", b: "Pengakuan berdasarkan pengalaman, tanpa ujian" },
      { aspect: "Bahasa", a: "Bahasa yang diujikan, termasuk Thai", b: "Bahasa yang belum diujikan" },
      { aspect: "Penerimaan instansi", a: "Luas", b: "Perlu dikonfirmasi lebih dulu" },
      { aspect: "Tertera di sertifikat", a: "Nomor penerjemah dan masa berlaku", b: "Nama klasifikasi dan masa berlaku" },
    ],
    chooseA: ["Semua berkas resmi pasangan bahasa Thai–Inggris"],
    chooseB: ["Hanya untuk bahasa yang belum memiliki ujian sertifikasi"],
    faqs: [
      { q: "Di mana memeriksa tingkat penerjemah?", a: "Cari nomor penerjemah di direktori naati.com.au untuk melihat klasifikasi dan masa berlakunya." + ID_TAIL },
      { q: "Apa bedanya dengan Certified Provisional?", a: "Itu tingkat awal karier dan sebagian instansi tidak menerimanya." },
      { q: "Tingkat apa yang Anda pakai?", a: "Pasangan Thai–Inggris ditangani Certified Translator, dengan nomor dan masa berlaku tercetak pada cap." },
    ],
  },
];

export const NAATI_COMPARE_LOCALE_ASIA: Record<"ja" | "ko" | "vi" | "id", NaatiCompareLocaleCopy> = {
  ja: {
    hreflang: "ja",
    hubTitle: "NAATI・領事認証・ノータリー・アポスティーユの違い比較",
    hubDescription:
      "NAATI認証翻訳とタイ外務省認証、ノータリー、アポスティーユ、宣誓翻訳、自己翻訳を1対1で比較。提出先に応じた最短ルートの選び方をまとめました。",
    hubH1: "NAATI認証翻訳と他の手続きの違いを比較",
    hubLead:
      "結論から言うと、NAATIは「訳文」を、タイ外務省領事局・公証業務弁護士・アポスティーユは「文書または署名」を証明します。オーストラリアとニュージーランドの機関は前者を、その他の提出先は後者を求めるのが一般的です。以下の各ページで項目ごとに比較し、最初の1回で正しい手続きを選べるようにしています。",
    labels: {
      table: "項目別の比較",
      chooseWhen: "こちらを選ぶ場合",
      faq: "よくあるご質問",
      other: "その他の比較",
      breadcrumbHome: "ホーム",
      breadcrumbNaati: "NAATI",
      breadcrumbCompare: "比較",
      disclaimer: "日数と費用は目安の範囲です。受理の可否は翻訳者ではなく提出先機関が判断します。",
      cta: "どのルートが必要か判断が難しい場合は、094-895-8999またはLINE @Thainotaryへ書類の写真をお送りください。ご注文前に担当者が確認します。",
    },
    items: JA_ITEMS,
  },
  ko: {
    hreflang: "ko",
    hubTitle: "NAATI·영사인증·공증·아포스티유 무엇이 필요한가",
    hubDescription:
      "NAATI 인증번역과 태국 외교부 인증, 공증변호사, 아포스티유, 선서번역, 자가번역을 1대1로 비교하고 제출처별 선택 기준을 정리했습니다.",
    hubH1: "NAATI 인증번역과 다른 절차 비교",
    hubLead:
      "결론부터 말하면 NAATI는 번역문을, 태국 외교부 영사국·공증변호사·아포스티유는 문서 또는 서명을 증명합니다. 호주와 뉴질랜드 기관은 대체로 전자를, 그 외 제출처는 후자를 요구합니다. 아래 페이지에서 항목별로 비교해 한 번에 올바른 절차를 선택하실 수 있도록 했습니다.",
    labels: {
      table: "항목별 비교",
      chooseWhen: "이럴 때 선택하세요",
      faq: "자주 묻는 질문",
      other: "다른 비교",
      breadcrumbHome: "홈",
      breadcrumbNaati: "NAATI",
      breadcrumbCompare: "비교",
      disclaimer: "기간과 비용은 대략적인 범위입니다. 접수 여부는 번역사가 아니라 제출 기관이 결정합니다.",
      cta: "어떤 절차가 필요한지 헷갈리시면 094-895-8999 또는 LINE @Thainotary로 서류 사진을 보내주세요. 주문 전에 담당자가 확인해 드립니다.",
    },
    items: KO_ITEMS,
  },
  vi: {
    hreflang: "vi",
    hubTitle: "NAATI, hợp pháp hóa lãnh sự, công chứng hay Apostille",
    hubDescription:
      "So sánh từng cặp giữa bản dịch NAATI với hợp pháp hóa lãnh sự Thái Lan, công chứng, apostille, biên dịch tuyên thệ và tự dịch, kèm cách chọn đúng ngay lần đầu.",
    hubH1: "So sánh bản dịch NAATI với các thủ tục khác",
    hubLead:
      "Nói ngắn gọn: NAATI chứng nhận bản dịch, còn Cục Lãnh sự Thái Lan, luật sư công chứng và apostille chứng nhận giấy tờ hoặc chữ ký. Cơ quan Úc và New Zealand hầu như luôn cần loại thứ nhất; phần lớn nước khác cần loại thứ hai. Các trang dưới đây so sánh từng mục để bạn đặt đúng dịch vụ ngay lần đầu.",
    labels: {
      table: "So sánh từng mục",
      chooseWhen: "Nên chọn khi",
      faq: "Câu hỏi thường gặp",
      other: "So sánh khác",
      breadcrumbHome: "Trang chủ",
      breadcrumbNaati: "NAATI",
      breadcrumbCompare: "So sánh",
      disclaimer: "Thời gian và chi phí chỉ là khoảng ước tính. Việc chấp nhận do cơ quan tiếp nhận quyết định, không phải biên dịch viên.",
      cta: "Chưa rõ hồ sơ của bạn cần theo hướng nào? Gọi 094-895-8999 hoặc LINE @Thainotary và gửi ảnh giấy tờ, đội ngũ của chúng tôi kiểm tra trước khi bạn đặt dịch vụ.",
    },
    items: VI_ITEMS,
  },
  id: {
    hreflang: "id",
    hubTitle: "NAATI, legalisasi konsuler, notarial, atau apostille",
    hubDescription:
      "Perbandingan berpasangan antara terjemahan NAATI dengan legalisasi Kemlu Thailand, advokat notarial, apostille, penerjemah tersumpah, dan terjemahan sendiri.",
    hubH1: "Membandingkan terjemahan NAATI dengan jalur lain",
    hubLead:
      "Ringkasnya: NAATI mengesahkan terjemahan, sedangkan Departemen Urusan Konsuler Thailand, advokat notarial, dan apostille mengesahkan dokumen atau tanda tangan. Instansi Australia dan Selandia Baru hampir selalu meminta yang pertama; tujuan lain umumnya meminta yang kedua. Halaman berikut membandingkan tiap pasangan baris demi baris agar Anda memesan layanan yang tepat sejak awal.",
    labels: {
      table: "Perbandingan baris demi baris",
      chooseWhen: "Pilih ini jika",
      faq: "Pertanyaan yang sering diajukan",
      other: "Perbandingan lain",
      breadcrumbHome: "Beranda",
      breadcrumbNaati: "NAATI",
      breadcrumbCompare: "Perbandingan",
      disclaimer: "Waktu dan biaya berupa kisaran perkiraan. Penerimaan ditentukan instansi penerima, bukan penerjemah.",
      cta: "Belum yakin jalur mana yang Anda butuhkan? Hubungi 094-895-8999 atau LINE @Thainotary dan kirim foto dokumennya; tim kami memeriksa sebelum Anda memesan.",
    },
    items: ID_ITEMS,
  },
};
